import api from './api';

export interface CashPosition {
  id: string;
  fund_id: string;
  date: string;
  currency: string;
  opening_balance: number;
  closing_balance: number;
  inflows: number;
  outflows: number;
  projected_balance?: number;
  account_type: string;
  bank_account_id?: string;
  notes?: string;
  net_cash_flow: number;
  balance_change: number;
  created_at: string;
  updated_at: string;
}

export interface CashPositionCreate {
  fund_id: string;
  date?: string;
  currency?: string;
  opening_balance?: number;
  closing_balance?: number;
  inflows?: number;
  outflows?: number;
  projected_balance?: number;
  account_type?: string;
  bank_account_id?: string;
  notes?: string;
}

export interface CashPositionsResponse {
  positions: CashPosition[];
  total: number;
  skip: number;
  limit: number;
}

export interface CashSummary {
  fund_id: string;
  current_balance: number;
  currency: string;
  last_update: string | null;
  has_data: boolean;
  metrics_7d: CashMetrics;
  metrics_30d: CashMetrics;
}

export interface CashMetrics {
  fund_id: string;
  period_days: number;
  total_inflows: number;
  total_outflows: number;
  net_cash_flow: number;
  average_balance: number;
  min_balance: number;
  max_balance: number;
  current_balance: number;
  positions_count: number;
}

class CashService {
  async getCashPositions(params?: {
    fund_id?: string;
    date_from?: string;
    date_to?: string;
    currency?: string;
    account_type?: string;
    skip?: number;
    limit?: number;
  }): Promise<CashPositionsResponse> {
    const response = await api.get('/cash/positions', { params });
    return response.data;
  }

  async getFundCashPositions(
    fundId: string,
    params?: {
      date_from?: string;
      date_to?: string;
      currency?: string;
      skip?: number;
      limit?: number;
    }
  ): Promise<CashPositionsResponse> {
    const response = await api.get(`/cash/positions/${fundId}`, { params });
    return response.data;
  }

  async getCurrentCashPosition(
    fundId: string,
    currency?: string
  ): Promise<CashPosition> {
    const response = await api.get(`/cash/positions/${fundId}/current`, {
      params: currency ? { currency } : {},
    });
    return response.data;
  }

  async createCashPosition(
    position: CashPositionCreate
  ): Promise<CashPosition> {
    const response = await api.post('/cash/positions', position);
    return response.data;
  }

  async getCashSummary(fundId: string): Promise<CashSummary> {
    const response = await api.get(`/cash/positions/${fundId}/summary`);
    return response.data;
  }

  async getCashMetrics(fundId: string, days: number = 30): Promise<CashMetrics> {
    const response = await api.get(`/cash/positions/${fundId}/metrics`, {
      params: { days },
    });
    return response.data;
  }

  // Projection methods
  async getProjections(
    fundId: string,
    params?: {
      date_from?: string;
      date_to?: string;
      method?: string;
    }
  ): Promise<{ fund_id: string; projections: any[]; total: number }> {
    const response = await api.get(`/cash/projections/${fundId}`, { params });
    return response.data;
  }

  async calculateProjections(
    fundId: string,
    daysAhead: number = 30,
    method: string = 'moving_average'
  ): Promise<any> {
    const response = await api.post(`/cash/projections/${fundId}/calculate`, null, {
      params: { days_ahead: daysAhead, method },
    });
    return response.data;
  }

  async getTrend(fundId: string, daysBack: number = 30): Promise<any> {
    const response = await api.get(`/cash/projections/${fundId}/trend`, {
      params: { days_back: daysBack },
    });
    return response.data;
  }
}

export const cashService = new CashService();
export const cashProjectionService = cashService;

